/**
 * A price only means something together with the date it applies to: a price table carries a
 * validity period, and a superseded one keeps serving the orders placed while it was current.
 * So every price the SPA renders carries its reference date, and the date is chosen explicitly
 * rather than taken from the browser clock deep inside a component.
 */

const TIME_ZONE = 'America/Sao_Paulo';

/**
 * `YYYY-MM-DD` in São Paulo, not in the viewer's zone. `toISOString().slice(0, 10)` is UTC, so at
 * 22:00 in Brasília it names *tomorrow* — and a buyer comparing the screen with a table that is
 * valid from tomorrow would be looking at a price that is not yet in force.
 */
export function todayInSaoPaulo(now: Date = new Date()): string {
  // Assembled from parts rather than read off a locale's layout: `en-CA` was used for being
  // `YYYY-MM-DD`, but that layout is ICU data, not a guarantee — ICU 72 (Node 18, and browsers
  // shipping it) writes `MM/DD/YYYY` for the same locale, and every dated price request then
  // carried a string that is not a date. The format call is only the timezone conversion.
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: TIME_ZONE,
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? '';

  return `${part('year')}-${part('month')}-${part('day')}`;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u;

/** True for a well-formed `YYYY-MM-DD` that is also a real calendar date. */
export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) {
    return false;
  }

  // `new Date('2025-02-30')` rolls over to March rather than failing, so the round trip is the
  // check: a date that does not survive it was never a date.
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * Renders an ISO date as `dd/mm/aaaa` without going through `formatDate`, which would apply the
 * São Paulo offset a second time: `2025-03-12` is already a calendar date, not an instant, and
 * re-zoning it can move it a day.
 */
export function formatIsoDate(value: string): string {
  if (!isIsoDate(value)) {
    return value;
  }
  const [year, month, day] = value.split('-');
  return `${day ?? ''}/${month ?? ''}/${year ?? ''}`;
}

/** The sentence shown next to a price: "Preço em 12/03/2025". */
export function describeReferenceDate(value: string): string {
  return `Preço em ${formatIsoDate(value)}`;
}

/** Turns an ISO timestamp (`valid_from`, `published_at`) into the `YYYY-MM-DD` a date input takes. */
export function toIsoDate(value: string | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

/**
 * Turns the `YYYY-MM-DD` a date input produces into the instant the API stores. Start of day in
 * São Paulo (UTC−3): a period typed as "valid from the 12th" that is sent as midnight UTC starts
 * on the 11th at 21:00 local, which is a whole evening of the wrong price.
 */
export function isoDateToTimestamp(value: string): string {
  return `${value}T00:00:00-03:00`;
}

/**
 * Turns the `YYYY-MM-DD` a date input produces into the instant to *read* a price at.
 *
 * Not the same instant as {@link isoDateToTimestamp}, and the difference is the whole point.
 * That one answers "from when is this price valid", so it is the start of the day. This one
 * answers "what does this customer pay on that day", and the start of the day is the wrong
 * moment to ask: a table published at four in the afternoon was not in force at midnight, so
 * every price would read as unpublished until the following day.
 *
 * For today the answer is *now*, which is what a buyer looking at a catalogue means. For any
 * other date it is the end of that day in São Paulo, so the answer is the last price that was
 * in force on it.
 */
export function isoDateToReadInstant(value: string, now: Date = new Date()): string {
  return value === todayInSaoPaulo(now) ? now.toISOString() : `${value}T23:59:59.999-03:00`;
}
